import withRouter from "Components/Common/withRouter"
import EmployeeDetailsTemplate from "Components/templates/hr/employee-details"
import useManual from "hooks/useManual"
import { useEffect, useState } from "react"
import { useDispatch } from "react-redux"
import { useSelector } from "react-redux"
import { useSearchParams } from "react-router-dom"
import { toast } from "react-toastify"
import { createSelector } from "reselect"
import { EmployeesSliceState, setEmployee } from "slices/human-resources/employees/reducer"
import { getEmployee, updateEmployeeWillingToRelocate, updateEmployeeEsaExempt, updateEmployeeContractEndDate, deleteConsultant } from "slices/thunk"
import { navigateToEditConsultants, navigateToEmployeeDetails, navigateToEmployeeList } from "utils/navigator"
import AreYouSureModal from "Components/organisms/are-you-sure-modal"
import { EMPLOYEE_TYPES } from "models/employee-type"
import { SCREENS } from "models/screen"
import { PermissionsSliceState } from "slices/auth/permissions/reducer"
import { useMemo } from "react"

const selectEmployeeList = createSelector(
    (state: any) => state.Employees,
    ({ employee }: EmployeesSliceState) => ({
        employee
    })
)

const selectPermissions = createSelector(
    (state: any) => state.Permissions,
    ({ screens }: PermissionsSliceState) => ({ screens })
)

const EmployeePage = (props: any) => {
    document.title = "Employee Details | IbexIQ";
    const { handleOpenManual } = useManual("human-resources/employees");
    const dispatch: any = useDispatch();
    const { employee } = useSelector(selectEmployeeList)
    const [searchParams] = useSearchParams();

    const [isEditing, setIsEditing] = useState(false)
    const [willingToRelocate, setWillingToRelocate] = useState(false)
    const [esaExempt, setEsaExempt] = useState(false)
    const [contractEndDate, setContractEndDate] = useState("")
    const [isLoadingEmployee, setIsLoadingEmployee] = useState(true)
    const [showDeleteModal, setShowDeleteModal] = useState(false)
    const [isDeleting, setIsDeleting] = useState(false)
    const { screens } = useSelector(selectPermissions)

    // Consultants (ICs) are edited on their own form; employees come from Dayforce and only a
    // few fields are editable here. Either way this is the only Edit button.
    const isConsultant = employee?.employeeType?.name === EMPLOYEE_TYPES.CONSULTANT
        || employee?.employeeType?.name === EMPLOYEE_TYPES.POTENTIAL_CONSULTANT
    const canManageConsultants = useMemo(() => screens.some((s: any) => s.code === SCREENS.HR_CONSULTANTS), [screens])

    const toDateInputValue = (date?: Date | string | null) => date ? date.toString().split('T')[0] : ""

    // Load employee
    const initializePage = async (employeeId: string) => {
        const emp = await getEmployee(employeeId);

        if (!emp) {
            navigateToEmployeeList(props.router.navigate);
            return;
        }

        dispatch(setEmployee(emp));
        setWillingToRelocate(emp.willingToRelocate);
        setEsaExempt(emp.esaExempt ?? false);
        setContractEndDate(toDateInputValue(emp.contractEndDate));
        setIsLoadingEmployee(false);
    }


    useEffect(() => {
        const empId = searchParams.get("empId");
        if (empId) {
            initializePage(empId)
        } else {
            toast.error("Something went wrong. Employee Id not found")
            navigateToEmployeeList(props.router.navigate)
        }
    }, [searchParams])

    const handleEdit = () => {
        if (!employee) return;
        if (isConsultant && canManageConsultants) {
            navigateToEditConsultants(props.router.navigate, employee.id)
            return
        }
        setIsEditing(true);
    }

    const handleDelete = async () => {
        if (!employee) return
        setIsDeleting(true)
        const ok = await deleteConsultant(employee)
        setIsDeleting(false)
        setShowDeleteModal(false)
        if (ok) {
            dispatch(setEmployee(null))
            navigateToEmployeeList(props.router.navigate)
        }
    }

    const handleCancel = async () => {
        if (!employee) return;
        setWillingToRelocate(employee.willingToRelocate ?? false);
        setEsaExempt(employee.esaExempt ?? false);
        setContractEndDate(toDateInputValue(employee.contractEndDate));
        setIsEditing(false);
    }

    const handleSave = async () => {
        if (!employee) return;

        const updates: Promise<boolean>[] = [];

        if (willingToRelocate !== (employee.willingToRelocate ?? false)) {
            updates.push(updateEmployeeWillingToRelocate(employee.id, willingToRelocate));
        }

        if (esaExempt !== (employee.esaExempt ?? false)) {
            updates.push(updateEmployeeEsaExempt(employee.id, esaExempt));
        }

        if (contractEndDate !== toDateInputValue(employee.contractEndDate)) {
            updates.push(updateEmployeeContractEndDate(employee.id, contractEndDate || null));
        }

        const results = await Promise.all(updates);
        if (results.some(ok => !ok)) return;

        dispatch(setEmployee({ ...employee, willingToRelocate, esaExempt, contractEndDate: new Date(contractEndDate) || null }));
        setIsEditing(false);
    }

    const handleBack = () => {
        props.router.navigate(-1);
        dispatch(setEmployee(null))
    }

    return <EmployeeDetailsTemplate
        handleBack={handleBack}
        handleOpenHelpDocument={handleOpenManual}
        onEdit={handleEdit}
        onCancel={handleCancel}
        onSave={handleSave}
        employee={employee}
        isLoadingEmployee={isLoadingEmployee}
        onDelete={isConsultant && canManageConsultants ? () => setShowDeleteModal(true) : undefined}
        onClickEmployee={(id) => navigateToEmployeeDetails(props.router.navigate, id)}
        modal={<AreYouSureModal
            isOpen={showDeleteModal}
            onModalClose={async () => setShowDeleteModal(false)}
            modalTitle="Delete Consultant"
            subTitle="Delete this consultant from IbexIQ."
            handleAreYouSureAccept={handleDelete}
            handleAreYouSureDecline={async () => setShowDeleteModal(false)}
            areYouSureacceptText={isDeleting ? 'Deleting...' : 'Delete'}
            areYouSuredeclineText="Cancel"
        />}
        isEditing={isEditing}
        willingToRelocate={isEditing ? willingToRelocate : (employee?.willingToRelocate ?? false)}
        onChangeWillingToRelocate={(value) => {
            setWillingToRelocate(value);
        }}
        esaExempt={isEditing ? esaExempt : (employee?.esaExempt ?? false)}
        onChangeEsaExempt={setEsaExempt}
        contractEndDate={isEditing ? contractEndDate : toDateInputValue(employee?.contractEndDate)}
        onChangeContractEndDate={(value) => {
            setContractEndDate(value);
        }}
    />
}


export default withRouter(EmployeePage);